import type { VNode } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import PtMessage from '@ui/message';
import profileApis from '@apis/config/graphql/profile';
import { useProfileList } from '@models/profile/profileList';
import { getProductList, Product, ProductKey, Profile } from '@models/profile/useCurrentProfile';
import useSWR from '@uiv1/libs/pt-swr';
import PtIcon from '@uiv1/components/icon';
import PtPopper from '@uiv1/components/popper';
import PtButton from '@uiv1/components/button';
import styles from './index.module.scss';
import { useStore } from 'vuex';
import i18n from '@assets/i18n/index';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import useUserInfo from '@apis/hooks/account/useUserInfo';
import PtMessageBox from '@ui/message-box';
import intercomService from '@assets/services/intercom.service';
import analyticsService from '@assets/services/analytics.service';

let inActive: boolean = false;
export default function useActiveProduct() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { profileList, queryProfileListLoading, getProfileList } = useProfileList();
    const { setProfileInfo } = useProfileInfo();
    const { account } = useAccountInfo();
    const { region } = useUserInfo();
    const { fetch: activeProduct, error } = useSWR(profileApis.activeProductInProfile, {
        responsive: false
    });

    const cancel = async () => {
        const overlay = document.querySelector(`.${styles.messageBoxBody}`);
        const closeBtn = overlay?.querySelector('.pt-message-box__headerbtn') as HTMLButtonElement;
        closeBtn?.click?.();
    };

    const active = async (sid?: string, product?: Product) => {
        if (!sid || !product || queryProfileListLoading.value) return;

        if (product.key === 'landing_page') {
            cancel();
            return;
        }

        const activeSuccessCb = (newProfile?: Profile) => {
            if (profileList.value) getProfileList();
            getProfileList(() => {
                setProfileInfo(route.params?.sid);
            });

            PtMessage.success(i18n?.global?.t('common.tips.activation_success'));
            cancel();

            analyticsService
                .usePTX('product_activate', {
                    product: product?.key === 'insight' ? 'pti' : 'ptx'
                })
                .track();

            setTimeout(() => {
                router.push({ name: product.routerName, params: { sid, updatePermission: 1 } });
            }, 32);
        };

        const overlay = document.querySelector(`.${styles.messageBoxBody}`);
        const activeBtn = overlay?.querySelector('.pt-button') as HTMLButtonElement;
        activeBtn.classList.add('is-loading-cover', 'is-disabled');
        const response = await activeProduct({
            where: { sid, productIndex: product.productIndex }
        });
        activeBtn.classList.remove('is-loading-cover', 'is-disabled');
        if (!response) {
            if (error.value?.code === 'PROFILE_HAS_BEEN_EXIS') {
                activeSuccessCb();
                return;
            }
            if (error.value?.code === 'PROFILE_LIMIT_ERR') {
                PtMessageBox({
                    title: i18n?.global?.t('common.no_auth_tip_title'),
                    cancelButtonText: i18n?.global?.t('common.button_cancel'),
                    confirmButtonText: i18n?.global?.t('form.link.contact_us'),
                    showCancelButton: true,
                    showConfirmButton: true,
                    showClose: true,
                    callback: () => intercomService?.contact(),
                    message:
                        i18n?.global?.t('common.upgrade_con') +
                        (region === 'EN' ? i18n?.global?.t('common.upgrade_con1_en') : i18n?.global?.t('common.upgrade_con1'))
                });
                return;
            }
            if (error.value?.code?.errorCode?.message ?? error.value?.message)
                PtMessage.error(error.value?.code?.errorCode?.message ?? error.value?.message);
            else PtMessage.error(i18n?.global?.t('common.tips.activation_error'));
            getProfileList();
        } else activeSuccessCb(response);
    };

    /**
     * 激活档案下产品
     * @param sid 档案sid
     * @param productKey "insight" | "experience"
     */
    const activeConfirm = (sid: string, productKey: ProductKey) => {
        if (inActive) return;
        const targetProfile = profileList.value?.find(item => item.sid === sid);
        if (!targetProfile) return;
        const targetProduct = getProductList().find(product => product.key === productKey);
        if (!targetProduct) return;

        const isOwner = targetProfile.ownerEmail === account.value?.email;
        const isLp = targetProduct.key === 'landing_page';
        // if (!isOwner) {
        //     showDialog({
        //         title: '- 权限 -',
        //         desc: `当前档案(${targetProfile.name}), 您没有权限激活，请联系档案所有者${targetProfile.ownerEmail}去激活。`,
        //         confirmButton: i18n?.global?.t('form.button.close')
        //     });
        //     return;
        // }

        inActive = true;

        setTimeout(() => {
            const popper = document.querySelector('[data-tippy-root]') as HTMLElement;
            if (!popper) return;
            popper?.remove?.();
        }, 32);

        PtMessageBox({
            showCancelButton: false,
            showConfirmButton: true,
            title: '',
            customClass: styles.messageBoxBody,
            callback: () => (inActive = false),
            message: (
                <>
                    <div class={styles.contentLeftWrapper}>
                        <img
                            src={
                                targetProduct.activeBg[
                                (i18n?.global?.locale?.toLowerCase?.() || 'jp') as 'jp'
                                ]
                            }
                        ></img>
                    </div>
                    <div class={styles.contentRightWrapper}>
                        <div class={styles.contentRight}>
                            <PtIcon icon={targetProduct.iconLogo} class={styles.logo}></PtIcon>
                            <p class={styles.text}>
                                {i18n?.global?.t(`product_list.use_active_product_${targetProduct.key}_desc`, {
                                    email: targetProfile.ownerEmail
                                })}
                            </p>
                            <ul class={styles.ul}>
                                <li class={styles.li}>
                                    {i18n?.global?.t(
                                        `product_list.use_active_product_${targetProduct.key}_desc_01`
                                    )}
                                </li>
                                <li class={styles.li}>
                                    {i18n?.global?.t(
                                        `product_list.use_active_product_${targetProduct.key}_desc_02`
                                    )}
                                </li>
                                <li class={styles.li}>
                                    {i18n?.global?.t(
                                        `product_list.use_active_product_${targetProduct.key}_desc_03`
                                    )}
                                </li>
                                <li class={styles.li}>
                                    {i18n?.global?.t(
                                        `product_list.use_active_product_${targetProduct.key}_desc_04`
                                    )}
                                </li>
                            </ul>
                            <PtPopper placement="top" zIndex={4000} disabled={isOwner}>
                                {{
                                    default: () => (
                                        <span class="pt-tooltip">
                                            {i18n?.global?.t('product_list.use_active_product_btn_tips')}
                                        </span>
                                    ),
                                    reference: () => (
                                        <span style="display:inline-block; width:100%;">
                                            <PtButton
                                                disabled={!isOwner && !isLp}
                                                size="medium"
                                                color={targetProduct.theme}
                                                fullfilled
                                                onClick={() => active(sid, targetProduct)}
                                            >
                                                {targetProduct.key === 'insight'
                                                    ? i18n?.global?.t('product_list.btn_to_active')
                                                    : isLp
                                                        ? i18n?.global?.t('product_list.use_active_got_btn')
                                                        : i18n?.global?.t('product_list.use_active_product_btn')}
                                            </PtButton>
                                        </span>
                                    )
                                }}
                            </PtPopper>
                            {isLp ? (
                                ''
                            ) : (
                                <div class={styles.tips}>
                                    <span class={styles.tipsStar}>*</span>
                                    <span
                                        v-html={
                                            targetProduct.key === 'insight'
                                                ? i18n?.global?.t(
                                                    'product_list.use_active_product_insight_sub_btn_text',
                                                    {
                                                        profileName: targetProfile.name
                                                    }
                                                )
                                                : isLp
                                                    ? ''
                                                    : i18n?.global?.t(
                                                        'product_list.use_active_product_experience_sub_btn_text',
                                                        {
                                                            profileName: targetProfile.name
                                                        }
                                                    )
                                        }
                                    ></span>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) as VNode
        })
            .then(e => { })
            .catch(e => { });
    };

    return { activeConfirm };
}
